import React from 'react';
import { TEXT, TEXT2, TEXT3, BLUE, SUCCESS, WARNING, DANGER, BORDER, FONT } from '../../shared';

// SEO audit result widget — renders structured audit data returned from the
// brain's SEO MCP tools. Backend shape:
//   {
//     type: 'seo_audit',
//     data: {
//       url, score (0-100), grade,
//       summary: string,
//       categories: [{ label, score, issues: [{ severity, message, fix? }] }],
//       recommendations: [string]
//     }
//   }

const SEVERITY = {
  critical: { color: DANGER, bg: `${DANGER}18`, label: 'Critical' },
  high:     { color: DANGER, bg: `${DANGER}18`, label: 'High' },
  warning:  { color: WARNING, bg: `${WARNING}1F`, label: 'Warning' },
  medium:   { color: WARNING, bg: `${WARNING}1F`, label: 'Medium' },
  info:     { color: BLUE,    bg: `${BLUE}18`,    label: 'Info' },
  low:      { color: BLUE,    bg: `${BLUE}18`,    label: 'Low' },
  good:     { color: SUCCESS, bg: `${SUCCESS}18`, label: 'Good' },
};

function scoreColor(score) {
  if (score == null) return TEXT3;
  if (score >= 85) return SUCCESS;
  if (score >= 60) return WARNING;
  return DANGER;
}

function ScoreRing({ score, size = 72 }) {
  const safe = Math.max(0, Math.min(100, Number(score) || 0));
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (safe / 100) * c;
  const color = scoreColor(safe);
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`${BORDER}`} strokeWidth="6" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%" y="52%"
        dominantBaseline="middle" textAnchor="middle"
        style={{ fontFamily: FONT, fontWeight: 700, fontSize: size * 0.32, fill: TEXT, fontFeatureSettings: "'tnum'" }}
      >
        {Math.round(safe)}
      </text>
    </svg>
  );
}

function CategoryRow({ cat }) {
  const color = scoreColor(cat.score);
  return (
    <div style={{
      padding: '12px 14px',
      borderRadius: 12,
      background: 'rgba(255,255,255,0.65)',
      border: '1px solid rgba(255,255,255,0.55)',
      marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: cat.issues?.length ? 8 : 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{cat.label}</span>
        {cat.score != null && (
          <span style={{
            fontSize: 11, fontWeight: 700, color,
            padding: '3px 8px', borderRadius: 999, background: `${color}1A`,
            fontFeatureSettings: "'tnum'",
          }}>
            {Math.round(cat.score)}/100
          </span>
        )}
      </div>
      {cat.issues?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {cat.issues.map((iss, i) => {
            const sev = SEVERITY[(iss.severity || 'info').toLowerCase()] || SEVERITY.info;
            return (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: TEXT2 }}>
                <span style={{
                  flexShrink: 0,
                  fontSize: 10, fontWeight: 700, color: sev.color,
                  background: sev.bg, padding: '2px 6px', borderRadius: 6,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  marginTop: 1,
                }}>
                  {sev.label}
                </span>
                <span style={{ lineHeight: 1.5 }}>
                  {iss.message}
                  {iss.fix && <span style={{ display: 'block', color: TEXT3, marginTop: 2 }}>Fix: {iss.fix}</span>}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SeoAuditWidget({ data, onAction }) {
  if (!data) return null;
  const score = data.score != null ? Number(data.score) : null;
  const color = scoreColor(score);
  const url = data.url || data.target || '';
  const grade = data.grade
    || (score == null ? '' : score >= 85 ? 'A' : score >= 75 ? 'B' : score >= 60 ? 'C' : score >= 40 ? 'D' : 'F');

  return (
    <div style={{
      background: 'rgba(255,255,255,0.55)',
      backdropFilter: 'blur(22px)',
      WebkitBackdropFilter: 'blur(22px)',
      border: '1px solid rgba(255,255,255,0.60)',
      borderRadius: 18,
      padding: 16,
      boxShadow: '0 1px 0 rgba(255,255,255,0.85) inset, 0 14px 30px -16px rgba(11,107,211,0.18)',
      width: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>
            SEO Audit
          </div>
          {url && (
            <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {url}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {score != null && <ScoreRing score={score} />}
          {grade && (
            <div style={{
              fontSize: 28, fontWeight: 800, color,
              fontFeatureSettings: "'tnum'", letterSpacing: '-0.02em',
              lineHeight: 1, minWidth: 28, textAlign: 'center',
            }}>
              {grade}
            </div>
          )}
        </div>
      </div>

      {data.summary && (
        <div style={{
          fontSize: 13, color: TEXT2, lineHeight: 1.55,
          marginBottom: 12,
          padding: '10px 12px',
          borderRadius: 10,
          background: `${BLUE}0E`,
          border: `1px solid ${BLUE}1F`,
        }}>
          {data.summary}
        </div>
      )}

      {data.categories?.length > 0 && (
        <div style={{ marginBottom: data.recommendations?.length ? 12 : 0 }}>
          {data.categories.map((cat, i) => <CategoryRow key={i} cat={cat} />)}
        </div>
      )}

      {data.recommendations?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            Recommendations
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: TEXT2, lineHeight: 1.6 }}>
            {data.recommendations.map((r, i) => <li key={i} style={{ marginBottom: 4 }}>{r}</li>)}
          </ol>
        </div>
      )}

      {onAction && (data.actions?.length > 0) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {data.actions.map((a, i) => (
            <button
              key={i}
              onClick={() => onAction({ id: a.id || a.label, label: a.label, prompt: a.prompt })}
              style={{
                padding: '8px 14px', borderRadius: 999,
                border: `1px solid ${BLUE}`, background: `${BLUE}10`, color: BLUE,
                fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
